import { StyleSheet, css } from 'aphrodite'
import { useEffect, useState } from 'react'

interface DatePickerProps {
  selected: Date | null
  onChange: (date: Date | null) => void
  onSelect: () => void
  minDate?: Date
  maxDate?: Date
  isOpen: boolean
  onClose: () => void
}

const DatePicker: React.FC<DatePickerProps> = ({
  selected,
  onChange,
  onSelect,
  minDate = new Date(),
  maxDate,
  isOpen,
  onClose,
}) => {
  const [currentDate, setCurrentDate] = useState(selected || new Date())

  // When opened with no selected date, show the first available month (minDate)
  useEffect(() => {
    if (!isOpen) return

    // If a date is already selected, prefer showing that month
    if (selected) {
      setCurrentDate(selected)
      return
    }

    // Otherwise, snap to the first available month based on minDate
    if (minDate) {
      const firstAvailableMonth = new Date(
        minDate.getFullYear(),
        minDate.getMonth(),
        1,
      )
      setCurrentDate(firstAvailableMonth)
    }
  }, [isOpen, selected, minDate])

  if (!isOpen) return null

  const daysInMonth = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth() + 1,
    0,
  ).getDate()

  const firstDayOfMonth = new Date(
    currentDate.getFullYear(),
    currentDate.getMonth(),
    1,
  ).getDay()

  const handlePrevMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() - 1),
    )
  }

  const handleNextMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() + 1),
    )
  }

  const handleDateClick = (day: number) => {
    const newDate = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      day,
    )

    // Use minDate prop for validation instead of hardcoded today
    const minDateCheck = new Date(minDate)
    minDateCheck.setHours(0, 0, 0, 0) // Reset time to start of day
    const maxDateCheck = maxDate ? new Date(maxDate) : undefined
    if (maxDateCheck) maxDateCheck.setHours(23, 59, 59, 999)

    const notBeforeMin = newDate >= minDateCheck
    const notAfterMax = !maxDateCheck || newDate <= maxDateCheck
    if (notBeforeMin && notAfterMax) {
      onChange(newDate)
    }
  }

  const renderCalendarDays = () => {
    const days = []
    const today = new Date()

    // Normalize min/max for comparison
    const minDateCheck = new Date(minDate)
    minDateCheck.setHours(0, 0, 0, 0)
    const maxDateCheck = maxDate ? new Date(maxDate) : undefined
    if (maxDateCheck) maxDateCheck.setHours(23, 59, 59, 999)

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(<div key={`empty-${i}`} className={css(styles.dayCell)} />)
    }

    // Add cells for each day of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        day,
      )
      date.setHours(0, 0, 0, 0) // Normalize for comparison

      const isSelected = selected?.toDateString() === date.toDateString()
      const isPast = date < minDateCheck
      const isAfter = maxDateCheck ? date > maxDateCheck : false
      const isToday = date.toDateString() === today.toDateString()

      days.push(
        <button
          key={day}
          onClick={() => handleDateClick(day)}
          disabled={isPast || isAfter}
          className={css(
            styles.dayCell,
            styles.dayButton,
            isSelected && styles.selectedDay,
            isPast && styles.pastDay,
            isAfter && styles.pastDay,
            isToday && styles.today,
          )}
        >
          {day}
        </button>,
      )
    }

    return days
  }

  return (
    <dialog
      className={css(styles.overlay)}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose()
        }
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          onClose()
        } else if (e.key === 'Enter' && e.target === e.currentTarget) {
          onClose()
        }
      }}
      tabIndex={-1}
      open
    >
      <div
        className={css(styles.container)}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <div className={css(styles.header)}>
          <button
            onClick={handlePrevMonth}
            onKeyDown={handlePrevMonth}
            className={css(styles.navButton)}
          >
            <i className="fa-solid fa-chevron-left" />
          </button>
          <span className={css(styles.monthYear)}>
            {currentDate.toLocaleString('default', {
              month: 'long',
              year: 'numeric',
            })}
          </span>
          <button onClick={handleNextMonth} className={css(styles.navButton)}>
            <i className="fa-solid fa-chevron-right" />
          </button>
        </div>

        <div className={css(styles.weekdays)}>
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
            <div key={day} className={css(styles.weekday)}>
              {day}
            </div>
          ))}
        </div>

        <div className={css(styles.calendar)}>{renderCalendarDays()}</div>

        <div className={css(styles.footer)}>
          <button
            onClick={onSelect}
            disabled={!selected}
            className={css(styles.selectButton)}
          >
            Select
          </button>
          <button onClick={onClose} className={css(styles.closeButton)}>
            Close
          </button>
        </div>
      </div>
    </dialog>
  )
}

const styles = StyleSheet.create({
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  container: {
    backgroundColor: 'white',
    borderRadius: '8px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    padding: '16px',
    width: '300px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  },
  monthYear: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#374151',
  },
  navButton: {
    padding: '8px',
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    color: '#6B7280',
    ':hover': {
      color: '#1C64F2',
    },
  },
  weekdays: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: '4px',
    marginBottom: '8px',
  },
  weekday: {
    textAlign: 'center',
    fontSize: '12px',
    fontWeight: 500,
    color: '#6B7280',
  },
  calendar: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: '4px',
  },
  dayCell: {
    aspectRatio: '1',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
  },
  dayButton: {
    border: 'none',
    backgroundColor: 'white',
    cursor: 'pointer',
    borderRadius: '4px',
    color: '#374151',
    ':hover': {
      backgroundColor: '#F3F4F6',
    },
  },
  selectedDay: {
    backgroundColor: '#1C64F2 !important',
    color: 'white !important',
  },
  pastDay: {
    color: '#D1D5DB',
    cursor: 'not-allowed',
    ':hover': {
      backgroundColor: 'transparent',
    },
  },
  today: {
    border: '1px solid #1C64F2',
  },
  footer: {
    marginTop: '16px',
    borderTop: '1px solid #E5E7EB',
    paddingTop: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  selectButton: {
    width: '100%',
    padding: '8px 16px',
    backgroundColor: '#1C64F2',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    ':hover': {
      backgroundColor: '#1955CC',
    },
    ':disabled': {
      backgroundColor: '#E5E7EB',
      cursor: 'not-allowed',
    },
  },
  closeButton: {
    width: '100%',
    padding: '8px 16px',
    backgroundColor: '#6B7280',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    ':hover': {
      backgroundColor: '#4B5563',
    },
  },
})

export default DatePicker
